import React, { useCallback, useEffect, useMemo, useState } from 'react';
import mixpanel from 'mixpanel-browser';
import { fetchWithMetaAttribution } from '../../utils/metaAttribution';
import { stageTikTokCompletePayment } from '../../utils/tiktokPixel';

export type ExitOfferLocale = 'en' | 'fr';

type BooleanSetter = React.Dispatch<React.SetStateAction<boolean>>;
type StringSetter = React.Dispatch<React.SetStateAction<string | null>>;
type NumberSetter = React.Dispatch<React.SetStateAction<number>>;

interface UseApplePayExitOfferOptions {
  stripePromise: PromiseLike<any>;
  country: string;
  currency: string;
  currencyCode: string;
  funnelId: string;
  locale: ExitOfferLocale;
  priceId: string;
  regularPriceId: string;
  uniqueUserId: string;
  creatingSubscription: boolean;
  requestInProgress: boolean;
  paymentProcessing: boolean;
  creatingSubscriptionRef: React.MutableRefObject<boolean>;
  requestInProgressRef: React.MutableRefObject<boolean>;
  setCreatingSubscription: BooleanSetter;
  setRequestInProgress: BooleanSetter;
  setPaymentProcessing: BooleanSetter;
  setCustomerEmail: StringSetter;
  setStep: NumberSetter;
  showToast: (message: string, type?: 'error' | 'success') => void;
}

const EXIT_OFFER_AMOUNT_CENTS = 99;
const API_BASE = typeof window !== 'undefined' && window.location.hostname === 'localhost'
  ? 'http://localhost:4242'
  : '';

const COPY = {
  en: {
    badge: 'Exclusive offer',
    titleWithDiscount: (discount: number) => `Save ${discount}% with this exclusive offer`,
    titleWithoutDiscount: 'A special offer just for you',
    description: 'Unlock every Premium watch face for one week at a special price.',
    benefits: [
      'Unlock every Premium watch face',
      'Get instant access after payment',
      'Cancel easily at any time',
    ],
    cta: (price: string) => `Pay ${price} with Apple Pay`,
    preparing: 'Preparing Apple Pay…',
    unavailable: 'Apple Pay is not available on this device.',
    missingPrice: 'This offer is still being configured.',
    terms: 'Introductory price for the first week. The regular subscription price applies after that. Cancel anytime.',
    decline: 'No thanks, decline this offer',
    closeLabel: 'Close offer',
    paymentError: 'Payment failed. Please try again.',
    applePayUnavailable: 'Apple Pay is currently unavailable. Please try again.',
    stripeLabel: 'Watchi Premium – Unlock Every Watch Face',
  },
  fr: {
    badge: 'Offre exclusive',
    titleWithDiscount: (discount: number) => `Économisez ${discount} % avec cette offre exclusive`,
    titleWithoutDiscount: 'Une offre spéciale rien que pour vous',
    description: 'Débloquez tous les cadrans Premium pendant une semaine à prix réduit.',
    benefits: [
      'Tous les cadrans Premium débloqués',
      'Accès immédiat après le paiement',
      'Résiliation simple à tout moment',
    ],
    cta: (price: string) => `Payer ${price} avec Apple Pay`,
    preparing: 'Préparation d’Apple Pay…',
    unavailable: 'Apple Pay n’est pas disponible sur cet appareil.',
    missingPrice: 'Cette offre est encore en cours de configuration.',
    terms: 'Tarif promotionnel pour la première semaine. Le tarif normal de l’abonnement s’appliquera ensuite. Résiliable à tout moment.',
    decline: 'Non merci, refuser cette offre',
    closeLabel: 'Fermer l’offre',
    paymentError: 'Le paiement a échoué. Veuillez réessayer.',
    applePayUnavailable: 'Apple Pay est momentanément indisponible. Veuillez réessayer.',
    stripeLabel: 'Watchi Premium – Débloquez tous les cadrans',
  },
};

const track = (event: string, data: Record<string, unknown> = {}) => {
  try {
    (mixpanel as any)?.track?.(event, data);
  } catch (_) {
    // Analytics must never block checkout.
  }
};

const trackCheckout = (value: number, currency: string, funnelId: string) => {
  try {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: `${funnelId} Subscription Exit Offer`,
        value,
        currency,
      });
    }
  } catch (_) {
    // Analytics must never block checkout.
  }
};

export const useApplePayExitOffer = ({
  stripePromise,
  country,
  currency,
  currencyCode,
  funnelId,
  locale,
  priceId,
  regularPriceId,
  uniqueUserId,
  creatingSubscription,
  requestInProgress,
  paymentProcessing,
  creatingSubscriptionRef,
  requestInProgressRef,
  setCreatingSubscription,
  setRequestInProgress,
  setPaymentProcessing,
  setCustomerEmail,
  setStep,
  showToast,
}: UseApplePayExitOfferOptions) => {
  const [isOpen, setIsOpen] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [applePayReady, setApplePayReady] = useState(false);
  const [applePayInitializing, setApplePayInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const copy = COPY[locale];
  const configured = priceId.trim().length > 0;

  const handlePaymentMethod = useCallback(async (ev: any) => {
    const eventPrefix = `${funnelId}_exit_offer_apple_pay`;
    track(`${eventPrefix}_paymentmethod_on`, {
      plan: 'premium',
      source: 'web',
      price: EXIT_OFFER_AMOUNT_CENTS / 100,
      currency: currencyCode,
    });

    if (creatingSubscriptionRef.current || requestInProgressRef.current) {
      ev.complete('fail');
      return;
    }

    creatingSubscriptionRef.current = true;
    requestInProgressRef.current = true;
    setCreatingSubscription(true);
    setRequestInProgress(true);
    setError(null);

    try {
      const fallbackEmail = `${uniqueUserId}@watchi.app`;
      const finalEmail = ev.payerEmail || fallbackEmail;
      const payerName = ev.payerName || uniqueUserId;
      const shippingCountry = ev.shippingAddress?.country || ev.paymentMethod?.billing_details?.address?.country || null;
      setCustomerEmail(finalEmail);

      const customerRes = await fetch(`${API_BASE}/api/stripe/create-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: finalEmail,
          userId: uniqueUserId,
          name: payerName,
          address: shippingCountry ? { country: shippingCountry } : undefined,
        }),
      });
      const customerJson = await customerRes.json();
      if (!customerRes.ok) throw new Error(customerJson?.error || copy.paymentError);
      const customerId = customerJson.customerId;

      try {
        const setupIntentRes = await fetch(`${API_BASE}/api/stripe/create-setup-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId,
            paymentMethodId: ev.paymentMethod.id,
          }),
        });
        if (!setupIntentRes.ok) {
          console.error(`[${eventPrefix}] SetupIntent failed`, await setupIntentRes.json());
        }
      } catch (setupIntentError) {
        console.error(`[${eventPrefix}] SetupIntent error`, setupIntentError);
      }

      const subscriptionRes = await fetchWithMetaAttribution(`${API_BASE}/api/stripe/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          paymentMethodId: ev.paymentMethod.id,
          priceId,
          regularPriceId,
          email: finalEmail,
          customerName: payerName,
          customerEmail: finalEmail,
          uniqueUserId,
          funnelId,
          eventSourceUrl: window.location.href,
          instantPayment: false,
        }),
      });

      if (!subscriptionRes.ok) {
        const errorData = await subscriptionRes.json();
        track(`${eventPrefix}_subscription_creation_error`, {
          plan: 'premium',
          source: 'web',
          currency: currencyCode,
        });

        if (errorData.error?.includes('already has an active subscription')) {
          ev.complete('success');
          track(`${eventPrefix}_flow_existing_subscription`, { plan: 'premium', source: 'web' });
          setIsOpen(false);
          setStep(5);
          return;
        }

        throw new Error(errorData.error || copy.paymentError);
      }

      await subscriptionRes.json();
      ev.complete('success');
      track(`${eventPrefix}_flow_success`, {
        plan: 'premium',
        source: 'web',
        price: EXIT_OFFER_AMOUNT_CENTS / 100,
        currency: currencyCode,
      });
      stageTikTokCompletePayment({
        content_id: priceId,
        content_type: 'product',
        content_name: 'Watchi Premium Subscription Exit Offer',
        quantity: 1,
        price: EXIT_OFFER_AMOUNT_CENTS / 100,
        value: EXIT_OFFER_AMOUNT_CENTS / 100,
        currency: currencyCode,
      });
      setIsOpen(false);
      setStep(5);
    } catch (paymentError) {
      ev.complete('fail');
      const message = paymentError instanceof Error ? paymentError.message : copy.paymentError;
      setError(message);
      showToast(message, 'error');
      track(`${eventPrefix}_paymentmethod_error`, { message, source: 'web' });
    } finally {
      creatingSubscriptionRef.current = false;
      requestInProgressRef.current = false;
      setCreatingSubscription(false);
      setRequestInProgress(false);
      setPaymentProcessing(false);
    }
  }, [
    copy.paymentError,
    creatingSubscriptionRef,
    currencyCode,
    funnelId,
    priceId,
    regularPriceId,
    requestInProgressRef,
    setCreatingSubscription,
    setCustomerEmail,
    setPaymentProcessing,
    setRequestInProgress,
    setStep,
    showToast,
    uniqueUserId,
  ]);

  useEffect(() => {
    let isActive = true;

    Promise.resolve(stripePromise)
      .then(async (stripe) => {
        if (!stripe || !isActive) {
          if (isActive) setApplePayInitializing(false);
          return;
        }

        const request = stripe.paymentRequest({
          country,
          currency,
          total: {
            label: copy.stripeLabel,
            amount: EXIT_OFFER_AMOUNT_CENTS,
          },
          requestPayerName: true,
          requestPayerEmail: true,
        });

        request.on('cancel', () => {
          track(`${funnelId}_exit_offer_apple_pay_cancel`, {
            price: EXIT_OFFER_AMOUNT_CENTS / 100,
            currency: currencyCode,
          });
          setPaymentProcessing(false);
        });

        request.on('paymentmethod', handlePaymentMethod);

        try {
          const result = await request.canMakePayment();
          if (!isActive) return;
          if (result?.applePay) {
            setPaymentRequest(request);
            setApplePayReady(true);
          } else {
            setApplePayReady(false);
          }
        } catch (initializationError) {
          if (isActive) {
            setApplePayReady(false);
            console.error(`[${funnelId}_exit_offer] Apple Pay initialization error`, initializationError);
          }
        } finally {
          if (isActive) setApplePayInitializing(false);
        }
      })
      .catch((initializationError) => {
        if (!isActive) return;
        setApplePayReady(false);
        setApplePayInitializing(false);
        console.error(`[${funnelId}_exit_offer] Stripe initialization error`, initializationError);
      });

    return () => {
      isActive = false;
    };
  }, [copy.stripeLabel, country, currency, currencyCode, funnelId, handlePaymentMethod, setPaymentProcessing, stripePromise]);

  const open = useCallback(() => {
    setError(null);
    setPaymentProcessing(false);
    setIsOpen(true);
    track(`${funnelId}_exit_offer_shown`, {
      price: EXIT_OFFER_AMOUNT_CENTS / 100,
      currency: currencyCode,
      source: 'apple_pay_cancel',
    });
  }, [currencyCode, funnelId, setPaymentProcessing]);

  const dismiss = useCallback((source: 'close_button' | 'backdrop' | 'decline' | 'escape') => {
    if (creatingSubscriptionRef.current || requestInProgressRef.current || paymentProcessing) return;
    track(`${funnelId}_exit_offer_dismissed`, { source });
    setIsOpen(false);
  }, [creatingSubscriptionRef, funnelId, paymentProcessing, requestInProgressRef]);

  const startApplePay = useCallback(async () => {
    if (paymentProcessing || creatingSubscription || requestInProgress) return;
    if (!configured) {
      setError(copy.missingPrice);
      return;
    }
    if (!paymentRequest || !applePayReady) {
      setError(copy.applePayUnavailable);
      return;
    }

    setError(null);
    setPaymentProcessing(true);
    track(`${funnelId}_exit_offer_cta_click`, {
      price: EXIT_OFFER_AMOUNT_CENTS / 100,
      currency: currencyCode,
      payment_method: 'apple_pay',
    });
    trackCheckout(EXIT_OFFER_AMOUNT_CENTS / 100, currencyCode, funnelId);

    try {
      await paymentRequest.show();
    } catch (showError) {
      setPaymentProcessing(false);
      const message = showError instanceof Error ? showError.message : copy.paymentError;
      if (!/cancel|abort/i.test(message)) {
        setError(message);
        track(`${funnelId}_exit_offer_apple_pay_show_error`, { message });
      }
    }
  }, [
    applePayReady,
    configured,
    copy.applePayUnavailable,
    copy.missingPrice,
    copy.paymentError,
    creatingSubscription,
    currencyCode,
    funnelId,
    paymentProcessing,
    paymentRequest,
    requestInProgress,
    setPaymentProcessing,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss('escape');
    };
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [dismiss, isOpen]);

  return {
    isOpen,
    open,
    dismiss,
    startApplePay,
    applePayReady,
    applePayInitializing,
    configured,
    error,
  };
};

interface ApplePayExitOfferModalProps {
  locale: ExitOfferLocale;
  currencyCode: string;
  country: string;
  originalAmountCents: number;
  isOpen: boolean;
  applePayReady: boolean;
  applePayInitializing: boolean;
  configured: boolean;
  busy: boolean;
  error: string | null;
  onDismiss: (source: 'close_button' | 'backdrop' | 'decline') => void;
  onPay: () => void;
}

export const ApplePayExitOfferModal: React.FC<ApplePayExitOfferModalProps> = ({
  locale,
  currencyCode,
  country,
  originalAmountCents,
  isOpen,
  applePayReady,
  applePayInitializing,
  configured,
  busy,
  error,
  onDismiss,
  onPay,
}) => {
  const copy = COPY[locale];
  const localeTag = locale === 'fr' ? 'fr-FR' : `en-${country}`;
  const formatter = useMemo(() => new Intl.NumberFormat(localeTag, {
    style: 'currency',
    currency: currencyCode,
  }), [currencyCode, localeTag]);
  const offerPrice = formatter.format(EXIT_OFFER_AMOUNT_CENTS / 100);
  const originalPrice = formatter.format(originalAmountCents / 100);
  const discount = originalAmountCents > EXIT_OFFER_AMOUNT_CENTS
    ? Math.round((1 - EXIT_OFFER_AMOUNT_CENTS / originalAmountCents) * 100)
    : 0;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="apple-pay-exit-offer-title"
    >
      <style>{`
        @keyframes watchi-exit-offer-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes watchi-exit-offer-panel-in {
          from { opacity: 0; transform: translateY(32px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .watchi-exit-offer-backdrop { animation: watchi-exit-offer-backdrop-in 180ms ease-out both; }
        .watchi-exit-offer-panel { animation: watchi-exit-offer-panel-in 320ms cubic-bezier(.22, 1, .36, 1) both; }
        @media (prefers-reduced-motion: reduce) {
          .watchi-exit-offer-backdrop, .watchi-exit-offer-panel { animation: none; }
        }
      `}</style>

      <div
        className="watchi-exit-offer-backdrop absolute inset-0 bg-[#1B3651] bg-opacity-60 backdrop-blur-md"
        onClick={() => onDismiss('backdrop')}
        aria-hidden="true"
      ></div>

      <div
        className="watchi-exit-offer-panel relative w-full max-w-md overflow-y-auto rounded-t-[32px] border border-[#C6B19E] border-opacity-10 shadow-2xl sm:rounded-[32px]"
        style={{
          maxHeight: 'calc(100dvh - 12px)',
          background: 'linear-gradient(165deg, #D7E4EE 0%, #F3ECDF 52%, #E2D6C6 100%)',
          fontFamily: 'Urbanist, sans-serif',
        }}
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]" aria-hidden="true">
          <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full" style={{ background: 'rgba(138, 103, 77, .15)', filter: 'blur(42px)' }}></div>
          <div className="absolute -left-24 top-44 h-52 w-52 rounded-full" style={{ background: 'rgba(190, 157, 112, .09)', filter: 'blur(48px)' }}></div>
        </div>

        <div className="relative px-6 pb-6 pt-3 sm:px-8 sm:pb-8">
          <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-[#D6E3ED] bg-opacity-90 sm:hidden"></div>
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => onDismiss('close_button')}
              disabled={busy}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#C6B19E] border-opacity-10 bg-[#FBF7F0] bg-opacity-90 text-[#1B3651] transition-colors hover:bg-opacity-20 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={copy.closeLabel}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="-mt-3 text-center">
            <div
              className="mx-auto mb-4 flex h-[76px] w-[76px] items-center justify-center rounded-[24px] border border-[#C6B19E] border-opacity-10 shadow-xl"
              style={{ background: 'linear-gradient(145deg, rgba(138, 103, 77, .26), rgba(190, 157, 112, .08))' }}
            >
              <img src="/Dialify_Icon.jpg" alt="Dialify logo" className="h-[58px] w-[58px] rounded-[16px] object-cover" />
            </div>

            <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-[.13em]" style={{ color: '#315F86', backgroundColor: 'rgba(138, 103, 77, .11)' }}>
              {copy.badge}
            </span>
            <h2 id="apple-pay-exit-offer-title" className="mx-auto mt-3 max-w-[350px] text-[30px] font-black leading-[1.02] tracking-[-.035em] text-[#1B3651]">
              {discount > 0 ? copy.titleWithDiscount(discount) : copy.titleWithoutDiscount}
            </h2>
            <p className="mx-auto mt-3 max-w-[340px] text-[15px] leading-5 text-[#1B3651] text-opacity-70">
              {copy.description}
            </p>
          </div>

          <div className="mt-5 flex items-end justify-center gap-3">
            {discount > 0 && <span className="pb-1 text-lg font-semibold text-[#1B3651] text-opacity-40 line-through">{originalPrice}</span>}
            <span className="text-[52px] font-black leading-none tracking-[-.055em]" style={{ color: '#315F86' }}>{offerPrice}</span>
          </div>

          <div className="mt-5 rounded-2xl border border-[#C6B19E] bg-[#FBF7F0] bg-opacity-75 px-4 py-3.5">
            {copy.benefits.map((benefit) => (
              <div key={benefit} className="flex items-center gap-3 py-1.5">
                <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full text-[#FDF9F2]" style={{ backgroundColor: '#315F86' }} aria-hidden="true">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="text-sm font-semibold text-[#1B3651] text-opacity-90">{benefit}</span>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={onPay}
            disabled={!configured || applePayInitializing || !applePayReady || busy}
            className="mt-5 flex h-16 w-full items-center justify-center rounded-xl bg-[#315F86] px-4 text-lg font-bold text-[#FDF9F2] shadow-xl transition-all hover:bg-[#254B6A] active:scale-[.985] disabled:cursor-not-allowed disabled:active:scale-100"
            aria-describedby="apple-pay-exit-offer-terms"
          >
            {busy ? (
              <span className="flex items-center justify-center gap-1.5" aria-label={copy.preparing}>
                <span className="h-2 w-2 animate-bounce rounded-full bg-[#FDF9F2]" style={{ animationDelay: '0ms', animationDuration: '1.4s' }}></span>
                <span className="h-2 w-2 animate-bounce rounded-full bg-[#FDF9F2]" style={{ animationDelay: '200ms', animationDuration: '1.4s' }}></span>
                <span className="h-2 w-2 animate-bounce rounded-full bg-[#FDF9F2]" style={{ animationDelay: '400ms', animationDuration: '1.4s' }}></span>
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span className="text-[27px] leading-none" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }} aria-hidden="true"></span>
                <span>{copy.cta(offerPrice)}</span>
              </span>
            )}
          </button>

          {applePayInitializing && <p className="mt-2 text-center text-xs text-[#1B3651] text-opacity-50">{copy.preparing}</p>}
          {!applePayInitializing && !applePayReady && <p className="mt-2 text-center text-xs text-[#5C7083]">{copy.unavailable}</p>}
          {!configured && <p className="mt-2 text-center text-xs text-[#5C7083]">{copy.missingPrice}</p>}
          {error && <p className="mt-2 rounded-lg bg-red-900 bg-opacity-30 px-3 py-2 text-center text-xs text-red-100">{error}</p>}

          <p id="apple-pay-exit-offer-terms" className="mx-auto mt-3 max-w-[350px] text-center text-[11px] leading-4 text-[#1B3651] text-opacity-45">
            {copy.terms}
          </p>
          <button
            type="button"
            onClick={() => onDismiss('decline')}
            disabled={busy}
            className="mx-auto mt-3 block px-4 py-2 text-sm font-semibold text-[#1B3651] text-opacity-50 underline decoration-[#A98769] decoration-opacity-20 underline-offset-4 transition-colors hover:text-opacity-80 disabled:cursor-not-allowed"
          >
            {copy.decline}
          </button>
        </div>
      </div>
    </div>
  );
};
